import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiCalendar, FiSave, FiImage } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Subtitle = styled.p`
  color: #666;
  margin: 10px 0 0 0;
  font-size: 14px;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const DayCard = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #C9A227;
  }
`;

const DayNumber = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #C9A227;
  margin-bottom: 15px;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #C9A227;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #C9A227;
  }
`;

const SaveButton = styled.button`
  background: #C9A227;
  color: white;
  border: none;
  padding: 15px 40px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 30px auto;
  transition: all 0.3s;

  &:hover {
    background: #b08f1f;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(201, 162, 39, 0.3);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const Message = styled.div`
  padding: 12px 20px;
  border-radius: 8px;
  margin: 20px auto;
  max-width: 600px;
  text-align: center;
  font-weight: 500;

  ${props => props.type === 'success' && `
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  `}

  ${props => props.type === 'error' && `
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  `}
`;

const CalendarManagement = () => {
  const [calendarDays, setCalendarDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  
  // Background settings state
  const [backgroundData, setBackgroundData] = useState({
    backgroundType: 'image',
    backgroundColor: '#000000',
    backgroundImageSize: 'auto',
    backgroundImagePosition: 'center center',
    backgroundImageRepeat: 'repeat'
  });
  const [backgroundImageFile, setBackgroundImageFile] = useState(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState(null);

  useEffect(() => {
    fetchCalendarData();
    fetchBackgroundData();
  }, []);

  const fetchCalendarData = async () => {
    try {
      const response = await fetch(`${API_URL}/calendar`);
      const result = await response.json();
      
      if (result.success) {
        setCalendarDays(result.data);
      }
    } catch (error) {
      console.error('Error fetching calendar:', error);
      showMessage('Failed to load calendar data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchBackgroundData = async () => {
    try {
      const response = await fetch(`${API_URL}/calendar-background`);
      const result = await response.json();
      
      if (result.success && result.data) {
        setBackgroundData({
          backgroundType: result.data.backgroundType,
          backgroundColor: result.data.backgroundColor,
          backgroundImageSize: result.data.backgroundImageSize,
          backgroundImagePosition: result.data.backgroundImagePosition,
          backgroundImageRepeat: result.data.backgroundImageRepeat
        });
        
        if (result.data.backgroundImage) {
          setBackgroundImagePreview(`${API_URL.replace('/api', '')}${result.data.backgroundImage}`);
        }
      }
    } catch (error) {
      console.error('Error fetching background:', error);
    }
  };

  const handleDayChange = (day, field, value) => {
    setCalendarDays(prev =>
      prev.map(d => d.day === day ? { ...d, [field]: value } : d)
    );
  };

  const handleBackgroundImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBackgroundImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBackgroundImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      // Save calendar days
      const calendarResponse = await fetch(`${API_URL}/calendar`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ days: calendarDays })
      });

      const calendarResult = await calendarResponse.json();

      if (!calendarResult.success) {
        showMessage('Failed to update calendar', 'error');
        setSaving(false);
        return;
      }
      
      // Save background settings
      const formData = new FormData();
      formData.append('backgroundType', backgroundData.backgroundType);
      formData.append('backgroundColor', backgroundData.backgroundColor);
      formData.append('backgroundImageSize', backgroundData.backgroundImageSize);
      formData.append('backgroundImagePosition', backgroundData.backgroundImagePosition);
      formData.append('backgroundImageRepeat', backgroundData.backgroundImageRepeat);
      
      if (backgroundImageFile) {
        formData.append('backgroundImage', backgroundImageFile);
      }
      
      const backgroundResponse = await fetch(`${API_URL}/calendar-background`, {
        method: 'PUT',
        body: formData
      });

      const backgroundResult = await backgroundResponse.json();

      if (backgroundResult.success) {
        showMessage('Calendar and background updated successfully!', 'success');
        setCalendarDays(calendarResult.data);
        setBackgroundImageFile(null);
      } else {
        showMessage('Calendar updated but background update failed', 'error');
      }
    } catch (error) {
      console.error('Error saving:', error);
      showMessage('Error saving changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  if (loading) {
    return (
      <Container>
        <Title><FiCalendar /> Loading calendar...</Title>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <div>
          <Title>
            <FiCalendar /> 31-Day Event Planning Calendar
          </Title>
          <Subtitle>
            Manage the 31-day event planning process displayed in the Services page calendar section
          </Subtitle>
        </div>
      </Header>

      {message.text && <Message type={message.type}>{message.text}</Message>}

      <Section>
        <SectionTitle>Calendar Section Background</SectionTitle>
        
        <FormGroup>
          <Label>Background Type</Label>
          <ToggleContainer>
            <ToggleButton
              active={backgroundData.backgroundType === 'color'}
              onClick={() => setBackgroundData({ ...backgroundData, backgroundType: 'color' })}
            >
              Color
            </ToggleButton>
            <ToggleButton
              active={backgroundData.backgroundType === 'image'}
              onClick={() => setBackgroundData({ ...backgroundData, backgroundType: 'image' })}
            >
              Image
            </ToggleButton>
          </ToggleContainer>
        </FormGroup>

        {backgroundData.backgroundType === 'color' ? (
          <FormGroup>
            <Label>Background Color</Label>
            <ColorInput
              type="color"
              value={backgroundData.backgroundColor}
              onChange={(e) => setBackgroundData({ ...backgroundData, backgroundColor: e.target.value })}
            />
            <ColorPreview color={backgroundData.backgroundColor}>
              {backgroundData.backgroundColor}
            </ColorPreview>
          </FormGroup>
        ) : (
          <>
            <FileInput>
              <input
                type="file"
                accept="image/*"
                onChange={handleBackgroundImageChange}
                id="calendar-bg-upload"
              />
              <label htmlFor="calendar-bg-upload">
                <FiImage /> {backgroundImageFile ? backgroundImageFile.name : 'Choose background image'}
              </label>
            </FileInput>
            {backgroundImagePreview && (
              <ImagePreview>
                <img src={backgroundImagePreview} alt="Background preview" />
              </ImagePreview>
            )}
            
            <FormGroup>
              <Label>Image Size</Label>
              <Select
                value={backgroundData.backgroundImageSize}
                onChange={(e) => setBackgroundData({ ...backgroundData, backgroundImageSize: e.target.value })}
              >
                <option value="cover">Cover (Fill entire area, may crop)</option>
                <option value="contain">Contain (Fit entire image, may show borders)</option>
                <option value="100% 100%">Stretch (Fill 100% width and height)</option>
                <option value="auto">Auto (Original size/Tiled)</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Image Position</Label>
              <Input
                type="text"
                value={backgroundData.backgroundImagePosition}
                onChange={(e) => setBackgroundData({ ...backgroundData, backgroundImagePosition: e.target.value })}
                placeholder="e.g., center center, top left"
              />
            </FormGroup>

            <FormGroup>
              <Label>Image Repeat</Label>
              <Select
                value={backgroundData.backgroundImageRepeat}
                onChange={(e) => setBackgroundData({ ...backgroundData, backgroundImageRepeat: e.target.value })}
              >
                <option value="no-repeat">No Repeat</option>
                <option value="repeat">Repeat Both</option>
                <option value="repeat-x">Repeat Horizontally</option>
                <option value="repeat-y">Repeat Vertically</option>
              </Select>
            </FormGroup>
          </>
        )}
      </Section>

      <CalendarGrid>
        {calendarDays.map(day => (
          <DayCard key={day.day}>
            <DayNumber>Day {day.day}</DayNumber>
            
            <FormGroup>
              <Label>Phase</Label>
              <Input
                type="text"
                value={day.phase}
                onChange={e => handleDayChange(day.day, 'phase', e.target.value)}
                placeholder="e.g., Phase 1: Concept & Creative Direction"
              />
            </FormGroup>

            <FormGroup>
              <Label>Title</Label>
              <Input
                type="text"
                value={day.title}
                onChange={e => handleDayChange(day.day, 'title', e.target.value)}
                placeholder="e.g., Day 1"
              />
            </FormGroup>

            <FormGroup>
              <Label>Description</Label>
              <TextArea
                value={day.description}
                onChange={e => handleDayChange(day.day, 'description', e.target.value)}
                placeholder="Brief description of this day's activities"
              />
            </FormGroup>

            <FormGroup>
              <Label>Day Range</Label>
              <Input
                type="text"
                value={day.days}
                onChange={e => handleDayChange(day.day, 'days', e.target.value)}
                placeholder="e.g., Days 1–4"
              />
            </FormGroup>
          </DayCard>
        ))}
      </CalendarGrid>

      <SaveButton onClick={handleSave} disabled={saving}>
        <FiSave /> {saving ? 'Saving...' : 'Save All Changes'}
      </SaveButton>
    </Container>
  );
};

const Section = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 20px 0;
`;

const ToggleContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const ToggleButton = styled.button`
  padding: 10px 20px;
  border: 2px solid ${props => props.active ? '#C9A227' : '#ddd'};
  background: ${props => props.active ? '#C9A227' : 'white'};
  color: ${props => props.active ? 'white' : '#666'};
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-color: #C9A227;
  }
`;

const ColorInput = styled.input`
  width: 80px;
  height: 40px;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
`;

const ColorPreview = styled.div`
  display: inline-block;
  margin-left: 15px;
  padding: 8px 15px;
  background: ${props => props.color};
  color: ${props => {
    // Calculate contrast for text color
    const hex = props.color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000' : '#fff';
  }};
  border-radius: 6px;
  font-weight: 600;
  font-size: 13px;
`;

const FileInput = styled.div`
  margin-bottom: 20px;

  input[type="file"] {
    display: none;
  }

  label {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 12px 20px;
    background: #f5f5f5;
    border: 2px dashed #ddd;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s;
    font-weight: 500;
    color: #666;

    &:hover {
      background: #fff;
      border-color: #C9A227;
      color: #C9A227;
    }

    svg {
      font-size: 20px;
    }
  }
`;

const ImagePreview = styled.div`
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;

  img {
    width: 100%;
    max-height: 200px;
    object-fit: cover;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #C9A227;
  }
`;

export default CalendarManagement;
